import {
  type ColumnDef,
  ColumnFiltersState,
  ColumnSizingState,
  Row,
  RowSelectionState,
  Table as TableType,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, {
  ForwardedRef,
  forwardRef,
  HTMLProps,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import {
  Card,
  DataTableDragCell,
  DataTableDragHeader,
  DataTableDragHeaderFilterProps,
  DataTablePagination,
  DataTableToolbar,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@ui/components';
import { cn } from '@ui/lib/utils';

export interface DataTableRef<TData> {
  getTable: () => TableType<TData>;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  calculatedRowClassName?: (row: TData, additionalProps?: Row<TData>) => string;
  getRowId?: (data: TData) => string;
  onExport?: (rows: string[]) => void;
  onPdf?: (rows: string[]) => void;
  onAddNew?: () => void;
  onDelete?: () => void;
  onDoubleClickRow?: (data: TData) => void;
  onClickRow?: (data: TData) => void;
  isLoading?: boolean;
  showFilter?: boolean;
  ToolbarRight?: React.ReactNode;
  enableMultiRowSelection?: boolean;
  setRowSelection?: (state: RowSelectionState) => void;
  rowSelection?: RowSelectionState;
  enableRowSelection?: boolean;
  hiddenColumns?: string[];
  hiddenPagination?: boolean;
  hiddenToolbar?: boolean;
  classNameCard?: HTMLProps<HTMLElement>['className'];
  classNameTable?: HTMLProps<HTMLElement>['className'];
  classNameHeader?: HTMLProps<HTMLElement>['className'];
  classNameRow?: HTMLProps<HTMLElement>['className'];
  classNameCell?: HTMLProps<HTMLElement>['className'];
  classNameCellHeader?: HTMLProps<HTMLElement>['className'];
  isDraggable?: boolean;
  perPage?: number;
  onImport?: (file: File) => void;
}
/**
 * @deprecated This component is deprecated and will be removed in a future version.
 * Please use AppTable component instead.
 * @see src/components/app-table
 */

//TODO: Fix approach to row selection
const DTable = <TData, TValue>(
  {
    columns,
    data,
    calculatedRowClassName,
    getRowId,
    onExport,
    onPdf,
    onAddNew,
    onDelete,
    onDoubleClickRow,
    onClickRow,
    classNameTable,
    classNameCard,
    classNameHeader,
    classNameRow,
    classNameCell,
    classNameCellHeader,
    isLoading,
    showFilter,
    ToolbarRight,
    hiddenColumns,
    rowSelection = {},
    setRowSelection = () => {},
    enableRowSelection = false,
    hiddenPagination = false,
    hiddenToolbar = false,
    isDraggable = false,
    perPage = undefined,
    onImport,
  }: DataTableProps<TData, TValue>,
  ref: ForwardedRef<DataTableRef<TData>>,
) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
    columns.map((c) => c.id!),
  );

  const defaultHidden: Record<string, boolean> = useMemo(
    () =>
      hiddenColumns?.reduce(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {},
    [hiddenColumns],
  );

  const [columnVisibility, setColumnVisibility] = useState(defaultHidden);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: perPage ?? 20,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(hiddenPagination
      ? {}
      : { getPaginationRowModel: getPaginationRowModel() }),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getRowId: getRowId ?? undefined,
    state: {
      globalFilter,
      columnPinning: { right: ['actions'], left: ['select'] },
      columnSizing,
      columnFilters,
      rowSelection,
      columnVisibility,
      columnOrder,
      ...(hiddenPagination ? {} : { pagination }),
    },
    ...(hiddenPagination ? {} : { onPaginationChange: setPagination }),
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: (updater) => {
      if (!setRowSelection) return;

      if (typeof updater === 'function') {
        setRowSelection(updater(rowSelection ?? {}));
      } else {
        setRowSelection(updater);
      }
    },
    onColumnOrderChange: setColumnOrder,
    enableRowSelection,
    enableMultiRowSelection: enableRowSelection,
    globalFilterFn: 'includesString',
    filterFromLeafRows: true,
    defaultColumn: {
      filterFn: 'includesString',
    },
    autoResetPageIndex: false,
  });

  useImperativeHandle(ref, () => ({
    getTable: () => table,
  }));

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  const handleDoubleClickRow = useCallback(
    (data: TData, column: ColumnDef<TData>) => {
      if (!onDoubleClickRow || column.id === 'actions') return;
      onDoubleClickRow(data);
    },
    [onDoubleClickRow],
  );

  const handleRowClick = useCallback(
    (data: TData, column: ColumnDef<TData>) => {
      if (!onClickRow || column.id === 'actions') return;
      onClickRow(data);
    },
    [onClickRow],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setColumnOrder((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id as string);
          const newIndex = columnOrder.indexOf(over.id as string);
          return arrayMove(columnOrder, oldIndex, newIndex);
        });
      }
    },
    [arrayMove],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      <Card className={cn('flex flex-col overflow-hidden', classNameCard)}>
        {!hiddenToolbar && (
          <DataTableToolbar
            searchValue={globalFilter}
            table={table}
            ToolbarRight={ToolbarRight}
            onAddNew={onAddNew}
            onDelete={
              Object.keys(rowSelection)?.length > 0 ? onDelete : undefined
            }
            onExcel={onExport}
            onImport={onImport}
            onPdf={onPdf}
            onSearch={setGlobalFilter}
          />
        )}
        <Table
          className={cn('w-full', !data.length && 'h-full', classNameTable)}
          style={{
            width:
              table.getTotalSize() < document.body.offsetWidth
                ? undefined
                : table.getTotalSize(),
            ...columnSizeVars,
          }}
        >
          <TableHeader
            className={cn(
              hiddenToolbar && '[&_tr]:border-t-0',
              classNameHeader,
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                <SortableContext
                  disabled={!isDraggable}
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <DataTableDragHeader
                      key={header.id}
                      classNameHeader={classNameCellHeader}
                      header={header}
                      isDraggable={isDraggable}
                    />
                  ))}
                </SortableContext>
              </TableRow>
            ))}
            {!!showFilter &&
              table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-inherit"
                  >
                    <SortableContext
                      disabled={!isDraggable}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => (
                        <DataTableDragHeaderFilterProps
                          key={header.id}
                          header={header}
                          isDraggable={isDraggable}
                        />
                      ))}
                    </SortableContext>
                  </TableRow>
                );
              })}
          </TableHeader>
          <TableBody className={cn('border-b')}>
            {table.getRowModel().rows?.length && !isLoading ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    calculatedRowClassName?.(row.original, row),
                    onDoubleClickRow || onClickRow
                      ? 'cursor-pointer'
                      : 'cursor-default',
                    classNameRow,
                  )}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <SortableContext
                      key={cell.id}
                      disabled={!isDraggable}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      <DataTableDragCell
                        key={cell.id}
                        cell={cell}
                        classNameCell={classNameCell}
                        styleCell={{
                          width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                        }}
                        onDoubleClick={handleDoubleClickRow}
                        onRowClick={handleRowClick}
                      />
                    </SortableContext>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="size-full hover:bg-inherit">
                <TableCell
                  className={cn('h-24 text-center', classNameCell)}
                  colSpan={columns.length}
                >
                  {isLoading ? <Spinner /> : 'No results.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {!hiddenPagination && (
          <DataTablePagination
            pageSizeOptions={perPage ? [perPage] : undefined}
            table={table}
          />
        )}
      </Card>
    </DndContext>
  );
};

export const DataTable = forwardRef(DTable) as <TData, TValue>(
  props: DataTableProps<TData, TValue> & {
    ref?: ForwardedRef<DataTableRef<TData>>;
  },
) => ReturnType<typeof DTable>;
